import { useCallback, useState } from "react";

import { modules, useFeature, useObjectMemo } from "byzantine";
import { useNavigate } from "react-router-dom";
import ApiHttp from "byzantine/src/ApiHttp";

import { PAYMENT_ACTIVITY_ROUTE } from "../../../../../../components/PaymentActivity/routes";

type UIState = "form" | "2FA" | "review";

export const useSendViaTemplate = () => {
  const { onContinue } = modules.wireTemplates.useSubmitSendViaTemplateForm();
  const { featureEnabled } = useFeature();
  const { createWireViaTemplate: onSubmit, loading } =
    modules.wireTemplates.useCreateWireViaTemplate();
  const { resetForm: resetSendViaTemplateForm } =
    modules.wireTemplates.sendViaTemplateForm.useForm();
  const { resetForm: resetMethodSelectorForm } =
    modules.wireTemplates.methodSelectorForm.useForm();
  const [uiState, setUiState] = useState<UIState>("form");
  const navigate = useNavigate();

  const submitReview = useCallback(
    (idempotencyKey: UUID) => {
      onSubmit(idempotencyKey, {
        onSuccess: (wire) => {
          resetSendViaTemplateForm();
          resetMethodSelectorForm();
          if (wire?.id && featureEnabled("payment_activity")) {
            navigate(`/${PAYMENT_ACTIVITY_ROUTE}/${wire.id}`);
          } else {
            navigate("/transfer");
          }
        },
        onError: () => setUiState("form"),
      });
    },
    [
      navigate,
      onSubmit,
      featureEnabled,
      resetSendViaTemplateForm,
      resetMethodSelectorForm,
    ]
  );

  const submitForm = useCallback(async () => {
    const next = () => setUiState("review");
    const trySudo = () =>
      ApiHttp.fetch("sudo")
        .then(() => setUiState("review"))
        .catch(() => setUiState("2FA"));
    onContinue(next, trySudo);
  }, [onContinue]);

  const complete2FA = useCallback(() => {
    setUiState("review");
  }, []);

  const resetFlow = useCallback(() => {
    setUiState("form");
  }, []);

  const cancel = useCallback(() => {
    navigate("/transfer");
  }, [navigate]);

  return useObjectMemo({
    uiState,
    submitForm,
    complete2FA,
    submitReview,
    resetFlow,
    loading,
    cancel,
  });
};
